import exceptionIcon from '@jetbrains/icons/exception.svg'
import ErrorMessage from '@jetbrains/ring-ui/components/error-message/error-message'
import {Tab, Tabs} from '@jetbrains/ring-ui/components/tabs/tabs'
import {Suspense} from 'react'
import type {EntryPointProps} from 'react-relay'
import {usePreloadedQuery} from 'react-relay'

import {getHrefWithQueryParams, getPipelinesHref} from '../../../../routes'
import type {Tab as TabType, TabId} from '../../../../types'
import {toBuildId} from '../../../../types'
import Loader from '../../../common/Loader/Loader'
import type {BuildArtifactsTabEntryPoint} from '../../BuildPage/BuildArtifactsTab/BuildArtifactsTab.entryPoint'
import type {BuildTestsTabEntryPoint} from '../../BuildPage/BuildTestsTab/BuildTestsTab.entryPoint'
import FullScreenErrorWrapper from '../components/FullScreenErrorWrapper/FullScreenErrorWrapper'
import Link from '../components/Link/Link'
import {serializeError} from '../utils/error'

import type {PipelineRunPagePipelineQuery} from './__generated__/PipelineRunPagePipelineQuery.graphql'
import PipelineRunHeader from './PipelineRunHeader/PipelineRunHeader'
import {pipelineRunTabs, PipelineRunTabs} from './PipelineRunPage.consts'
import {usePipelineRunPage} from './PipelineRunPage.hooks'
import {pipelineQuery} from './PipelineRunPage.queries'
import type {ChangesTabEntryPoint} from './PipelineTab/JobDetails/ChangesTab/ChangesTab.entryPoint'
import {JobGameTabContextContext} from './PipelineTab/JobDetails/GameTab/GameTab.contexts'
import {useJobGameTabContextValue} from './PipelineTab/JobDetails/GameTab/GameTab.hooks'
import PipelineTab from './PipelineTab/PipelineTab'

import styles from './PipelineRunPage.css'

type Queries = {pipeline: PipelineRunPagePipelineQuery}
export type NestedEntryPoints = {
  testsTab?: typeof BuildTestsTabEntryPoint
  artifactsTab?: typeof BuildArtifactsTabEntryPoint
  changesTab?: typeof ChangesTabEntryPoint
}
function PipelineRunPage({
  queries,
  entryPoints,
}: EntryPointProps<Queries, NestedEntryPoints, {}, {}>) {
  const {project = null} = usePreloadedQuery(pipelineQuery, queries.pipeline)
  const {jobId, buildError, pipelineError, searchParams, navigate, location, isPageDataLoading} =
    usePipelineRunPage()
  const jobGameTabContextValue = useJobGameTabContextValue()

  if (isPageDataLoading) {
    return <Loader />
  }

  const error = buildError ?? pipelineError
  if (error) {
    return (
      <FullScreenErrorWrapper>
        <ErrorMessage
          icon={exceptionIcon}
          message="Error while loading pipeline"
          description={serializeError(error)}
        >
          <Link to={getPipelinesHref()}>{'Go to the home page'}</Link>
        </ErrorMessage>
      </FullScreenErrorWrapper>
    )
  }

  const selectedTab = searchParams.get('pipelineRunTab') ?? pipelineRunTabs[0]?.id

  const renderTabContent = (tabId: TabId) => {
    if (tabId === PipelineRunTabs.PIPELINE) {
      return (
        <JobGameTabContextContext.Provider value={jobGameTabContextValue}>
          <PipelineTab jobId={jobId != null ? toBuildId(jobId) : null} entryPoints={entryPoints} />
        </JobGameTabContextContext.Provider>
      )
    }

    return null
  }

  const renderTab = (tab: TabType) => {
    const href = getHrefWithQueryParams(location, null, params => ({
      ...params,
      pipelineRunTab: tab.id,
    }))
    return (
      <Tab
        className={styles.tab}
        key={tab.id}
        id={tab.id}
        title={tab.title}
        href={href}
        titleProps={{
          onPlainLeftClick: () => navigate(href, {replace: selectedTab === tab.id}),
        }}
      >
        <div className={styles.tabContent}>
          <Suspense fallback={<Loader />}>{renderTabContent(tab.id)}</Suspense>
        </div>
      </Tab>
    )
  }

  const renderTabs = () => (
    <Tabs className={styles.tabs} selected={selectedTab}>
      {pipelineRunTabs.map(renderTab)}
    </Tabs>
  )

  return (
    <>
      <PipelineRunHeader pipelineKey={project} />
      {pipelineRunTabs.length > 1 ? renderTabs() : renderTab(pipelineRunTabs[0])}
    </>
  )
}

export default PipelineRunPage
export type PipelineRunPageComponent = typeof PipelineRunPage
